/**
 * Card Variant Tokens — CVA-ready card style presets.
 *
 * Defines semantic card variant objects that can be applied via
 * className merging (cn) or consumed by AppCard's `variant` prop.
 *
 * Each variant specifies: surface, border, hover behavior, and animation.
 *
 * Usage:
 *   import { cardVariants } from '@/design-system/tokens';
 *   <div className={cn(cardVariants.metric)}>...</div>
 *
 *   // Or with AppCard:
 *   <AppCard variant="metric">...</AppCard>
 */

export const cardVariants = {
  /** Default card — neutral surface, hairline border. */
  default: 'bg-surface-card border border-border-hairline rounded-lg',

  /** Metric / KPI card — subtle primary tint, hover lift. */
  metric: 'bg-surface-card border border-border-hairline rounded-lg animate-hover-lift',

  /** Analytics / chart container — clean, elevated on hover. */
  analytics: 'bg-surface-card border border-border-hairline rounded-lg',

  /** Workspace overview card — neutral elevated surface. */
  workspace: 'bg-surface-neutral-elevated border border-border-hairline rounded-lg',

  /** Contextual info card (right panel, detail rail). */
  context: 'bg-surface-panel border border-border-hairline rounded-lg',

  /** Status summary card — primary soft tint. */
  status: 'bg-surface-primary-soft border border-border-hairline rounded-lg',

  /** Patient/entity card — premium surface. */
  patient: 'bg-surface-premium border border-border-hairline rounded-lg',

  /** Queue / task list card — elevated surface. */
  queue: 'bg-surface-elevated border border-border-hairline rounded-lg',

  /** Chart wrapper card — clean with chart-reveal animation. */
  chart: 'bg-surface-card border border-border-hairline rounded-lg animate-chart-reveal',

  /** Interactive card with hover elevation. */
  interactive: 'bg-surface-card border border-border-hairline rounded-lg animate-hover-lift cursor-pointer',
} as const;

export type CardVariant = keyof typeof cardVariants;
